import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { BrowserRouter } from 'react-router-dom'
import { I18nextProvider } from 'react-i18next'
import i18n from '../../../../i18n'
import styled, { ThemeProvider } from 'styled-components'
import { appTheme } from 'spa/config/styled'
import Session from '../../../layouts/v2/Session'
import { session_path } from '../../../../routes'
import { WorkPreferenceFormFormGroupEnum } from '../../../api/generated'
import { FirstForm } from 'spa/member/components/WorkPreference/Registration/FirstForm'
import { SecondForm } from 'spa/member/components/WorkPreference/Registration/SecondForm'
import { FourthForm } from 'spa/member/components/WorkPreference/Registration/FourthForm'
import { WorkPreferenceFormNames } from 'spa/member/components/WorkPreference/constants'
import { Button } from 'spa/member/components/Button'
import { FormType } from 'spa/member/components/WorkPreference/Registration/types'
import { Flash } from 'javascripts/layouts/v2/Flash'
import FlashType from 'javascripts/layouts/v2/FlashType'
import { WorkPreferenceFormProps } from './types'

export const SessionContext = React.createContext<Session>(null)

type Props = {
  formGroup: WorkPreferenceFormFormGroupEnum
}

window.addEventListener('DOMContentLoaded', () => {
  if (!document.querySelector('body.controller--profile_sheets-information_disclosures')) return false

  const workPreferenceFormDiv = document.querySelector('[data-react-profile-sheet-work-preference-form]') as HTMLDivElement

  if (workPreferenceFormDiv) {
    const props = JSON.parse(workPreferenceFormDiv.dataset.reactProfileSheetWorkPreferenceForm)

    ReactDOM.render(
      <WorkPreferenceFormForSlim formGroup={props.formGroup} />,
      workPreferenceFormDiv
    )
  }
})

const FlashContainer = styled.div`
  position: fixed;
  top: var(--header-navbar-height, 4rem);
  left: 0;
  right: 0;
  z-index: 1000;
`

const StyledSaveButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
`

const StyledSaveButton = styled(Button)`
  background-color: ${props => props.theme.members.colors.lightGreen};
  color: ${props => props.theme.members.colors.white};
  width: 240px;
`

const WorkPreferenceTabs = styled.div`
  display: flex;
  column-gap: 2rem;
  border-bottom: 1px solid ${props => props.theme.members.colors.silver._30};
`

const WorkPreferenceTab = styled.a<{ active: boolean }>`
  font-size: .875rem;
  color: ${props => props.active ? props.theme.members.colors.appleGreen._60 : props.theme.members.colors.silver._70};
  text-decoration: none;
  cursor: ${props => props.active ? 'default' : 'pointer'};
  border-bottom: 2px solid ${props => props.active ? props.theme.members.colors.appleGreen._60 : 'transparent'};
  font-weight: 700;
  padding: 1rem 0;

  &:hover {
    color: ${props => props.theme.members.colors.appleGreen._60};
    opacity: ${props => props.active ? 1 : 0.7};
    text-decoration: none;
  }
`

const defaultArgs = { formType: 'PROFILESHEET' as FormType }

const WorkPreferenceForm: React.FC<WorkPreferenceFormProps> = ({ formGroup, onChangeStep, onSubmit, onError }) => {
  const args = { ...defaultArgs, onChangeStep, onSubmit, onError }

  switch (formGroup) {
    case WorkPreferenceFormFormGroupEnum.CurrentSituation:
      return <FirstForm {...args} />
    case WorkPreferenceFormFormGroupEnum.JobOfferPreference:
      return <SecondForm {...args} />
    case WorkPreferenceFormFormGroupEnum.AdditionalPreferences:
      return <FourthForm {...args} />
    default:
      return <FirstForm {...args} />
  }
}

const formName = (formGroup: WorkPreferenceFormFormGroupEnum) => {
  switch (formGroup) {
    case WorkPreferenceFormFormGroupEnum.CurrentSituation:
      return WorkPreferenceFormNames.CurrentSituation
    case WorkPreferenceFormFormGroupEnum.JobOfferPreference:
      return WorkPreferenceFormNames.DesiredProject
    case WorkPreferenceFormFormGroupEnum.AdditionalPreferences:
      return WorkPreferenceFormNames.Others
    default:
      return WorkPreferenceFormNames.CurrentSituation
  }
}

const isCurrentSituation = (formGroup: WorkPreferenceFormFormGroupEnum) => {
  return formGroup === WorkPreferenceFormFormGroupEnum.CurrentSituation
}

const isJobOfferPreference = (formGroup: WorkPreferenceFormFormGroupEnum) => {
  return formGroup === WorkPreferenceFormFormGroupEnum.JobOfferPreference
}

const isAdditionalPreferences = (formGroup: WorkPreferenceFormFormGroupEnum) => {
  return formGroup === WorkPreferenceFormFormGroupEnum.AdditionalPreferences
}

const WorkPreferenceFormForSlim: React.FC<Props> = (props) => {
  const [session, setSession] = useState<Session>(null)
  const [flash, setFlash] = useState<FlashType>({})
  const [sending, setSending] = useState(false)

  const formGroup = props.formGroup || WorkPreferenceFormFormGroupEnum.CurrentSituation

  useEffect(() => {
    void fetch(session_path())
      .then(response => response.json())
      .then((json: Session) => setSession(json))
  }, [])

  const onChangeStep = () => {
    setSending(false)
    window.scrollTo(0, 0)
    setFlash({ success: '保存しました' })
    setTimeout(() => setFlash({}), 3000)
  }

  const onSubmit = () => setSending(true)
  const onError = () => setSending(false)

  return (
    <I18nextProvider i18n={ i18n }>
      <SessionContext.Provider value={session}>
        <BrowserRouter>
          <ThemeProvider theme={appTheme}>
            <FlashContainer data-cy="flash-container">
              {<Flash flash={flash} />}
            </FlashContainer>
            <WorkPreferenceTabs>
              <WorkPreferenceTab
                href={isCurrentSituation(formGroup) ? undefined : '?form_group=current_situation' }
                active={isCurrentSituation(formGroup)}
                data-cy="current-situation-tab">
                現在のご状況
              </WorkPreferenceTab>
              <WorkPreferenceTab
                href={isJobOfferPreference(formGroup) ? undefined : '?form_group=job_offer_preference'}
                active={isJobOfferPreference(formGroup)}
                data-cy="job-offer-preference-tab">
                案件のご条件
              </WorkPreferenceTab>
              <WorkPreferenceTab
                href={isAdditionalPreferences(formGroup) ? undefined : '?form_group=additional_preferences'}
                active={isAdditionalPreferences(formGroup)}
                data-cy="additional-preference-tab">
                その他
              </WorkPreferenceTab>
            </WorkPreferenceTabs>
            <WorkPreferenceForm formGroup={formGroup} onChangeStep={onChangeStep} onSubmit={onSubmit} onError={onError} />
            <StyledSaveButtonContainer>
              <StyledSaveButton type="submit" form={formName(formGroup)} disabled={sending}>
                この内容で保存する
              </StyledSaveButton>
            </StyledSaveButtonContainer>
          </ThemeProvider>
        </BrowserRouter>
      </SessionContext.Provider>
    </I18nextProvider>
  )
}
